/**
 * Taskforce Spearhead — Light (8-Week, Intermediate)
 *
 * The intermediate-capability version of the Operation Spearhead program
 * (see taskforceSpearhead8Week.ts for the advanced flagship). Same job —
 * secure all four sectors against EREBUS by hitting every domain every week
 * — but scaled for an athlete who isn't ready for the 6-day flagship:
 *   - 5 training days (2 full rest days) instead of 6
 *   - Full-body strength days (lower total barbell load) instead of heavy
 *     lower/upper/power splits
 *   - Shorter, lighter ruck (2.5 mi @ 25 lb vs 4 mi @ 35 lb)
 *   - Shorter aerobic + conditioning doses; 3-round grinder vs 5
 *   - Pull-ups prescribed with a scaling path (inverted row / assisted)
 *
 * Sector map (same engine, lib/spearheadEngine.ts):
 *   ALPHA (strength tonnage)   → squat, RDL, press, row, KB swing
 *   BRAVO (endurance meters)   → run/walk, assault bike
 *   CHARLIE (bodyweight reps)  → pull-up, push-up, dip, burpee, plank
 *   DELTA (loaded carry)       → ruck, farmer/suitcase carry
 * Every sector is trained 3–5×/week so no sector falls behind. Location is
 * kept coherent: gym days stay in the gym (bike finisher for Bravo), the
 * aerobic day and ruck are outdoor/treadmill — never mixed in one session.
 */

import type { WorkoutProgram, WorkoutTemplateDay, ProgramMetadata, PeriodizationPhaseConfig } from '../types/program';

const metadata: ProgramMetadata = {
  id: 'taskforce-spearhead-light-8week',
  name: 'Taskforce Spearhead — Light',
  description: 'The intermediate Operation Spearhead program — five days a week that still push all four sectors: strength, engine, bodyweight, and loaded carries. Scaled volume, a shorter ruck, and a clear path up to the full Taskforce Spearhead.',
  level: 'specialist',
  duration: 8,
  daysPerWeek: 5,
  goals: ['tactical-performance', 'work-capacity', 'strength', 'endurance'],
  difficulty: 'intermediate',
  emphasis: 'Balanced four-domain hybrid at an intermediate dose: build the base to graduate to the full Taskforce Spearhead.',
  version: '1.0.0',
  createdAt: new Date(),
  updatedAt: new Date(),
  isActive: true,
  hasRangeDay: false,
  // ~90 min Z2/wk (aerobic day 28 + two 10-min gym finishers + the steady
  // 2.5-mi ruck) — real aerobic base without burying an intermediate athlete.
  weeklyZ2Target: 90,
  assessmentFormat: 'integrated',
  // Doable in a well-equipped home gym: barbell/DBs, KB, a bike or rower,
  // and a pull-up bar. Ruck + aerobic days are outdoor (or treadmill).
  requiredEquipment: 'home-gym',
};

// Lighter block periodization. No trainingParameters (rep-scaling path never
// triggers); 'progressive' ML owns the loading.
/**
 * NO DELOAD PHASE — deliberate, confirmed 2026-08-11.
 *
 * Ten other shipped programs author a `type: 'deload'` phase and the
 * generator now honours them (it previously read a phantom `program.schedule`
 * field and every scheduled deload was dead). Spearhead intentionally has
 * none: it is a fixed-length challenge block, not an open-ended training
 * cycle, and the streak/adherence mechanic is the point.
 *
 * Do NOT "fix" this by adding a deload phase — an audit will flag its absence
 * as an inconsistency, and it is not one.
 *
 * Fatigue is still covered: the reactive backstop in lib/recoveryDebt.ts
 * (>= 4 consecutive sessions averaging RPE >= 8.0 within the last 10) fires
 * independently of any program schedule and cuts volume 50% / intensity 70%.
 */
const periodizationPhases: PeriodizationPhaseConfig[] = [
  {
    name: 'Accumulation', type: 'accumulation', weeks: [1, 2, 3, 4],
    focus: 'Build the base across all four sectors',
    intensityMultiplier: 0.9, volumeMultiplier: 1.0,
    progression: { type: 'linear', startIntensity: 0.65, endIntensity: 0.75, weeklyIncrease: 0.033 },
  },
  {
    name: 'Intensification', type: 'intensification', weeks: [5, 6, 7],
    focus: 'Add intensity, hold sector balance',
    intensityMultiplier: 1.0, volumeMultiplier: 0.95,
    progression: { type: 'linear', startIntensity: 0.75, endIntensity: 0.82, weeklyIncrease: 0.025 },
  },
  {
    name: 'Deload', type: 'deload', weeks: [8],
    focus: 'Recover and consolidate territory',
    intensityMultiplier: 0.7, volumeMultiplier: 0.6,
    progression: { type: 'linear', startIntensity: 0.6, endIntensity: 0.6, weeklyIncrease: 0 },
  },
];

// ============================================================================
// PREP / RECOVERY — pattern-based, short, injury-augmentable
// ============================================================================

// PREP / RECOVERY
//
// One prep and one recovery per DAY, matched to that day's movement pattern.
// A single `generalPrep` used to cover days 1, 3 and 5 — three of five days
// opening identically, and none of them opening with what the session
// actually demanded (day 3 pressed overhead with no cuff work; day 5 did
// burpees and carries with no wrist or grip prep).

// DAY 1 — squat pattern + horizontal press + carry.
const squatPressPrep = {
  name: 'Movement Prep',
  type: 'prep' as const,
  exercises: [
    { name: 'Easy Cardio', duration: '3 min', format: 'prep', details: 'Bike or row — raise core temp' },
    { name: 'Ankle Rocks', sets: 1, reps: '10/side', format: 'prep', details: 'Dorsiflexion — goblet squat depth lives here' },
    { name: 'Glute Bridge', sets: 2, reps: '10', format: 'prep', details: 'Wake the posterior chain before squatting' },
    { name: 'Scapular Push-Ups', sets: 2, reps: 10, format: 'prep', details: 'Serratus + scap control before the bench and push-ups' },
    { name: 'Band Pull-Apart', sets: 2, reps: '15', format: 'prep', details: 'Balance the pressing volume' },
  ],
};

// DAY 3 — hinge + strict overhead + dips. The overhead work is why this day
// gets cuff and t-spine prep that day 1 does not.
const hingeOverheadPrep = {
  name: 'Movement Prep',
  type: 'prep' as const,
  exercises: [
    { name: 'Easy Cardio', duration: '3 min', format: 'prep', details: 'Bike or row — raise core temp' },
    { name: 'Leg Swings', sets: 2, reps: '10/side', format: 'prep', details: 'Loosen the hamstrings before the RDL' },
    { name: 'Cat-Cow', sets: 1, reps: '8', format: 'prep', details: 'Segmental spine control before hinging under load' },
    { name: 'T-Spine Extension', sets: 1, reps: '8', format: 'prep', details: 'You cannot press overhead through a stiff thoracic spine' },
    { name: 'Band External Rotation', sets: 2, reps: '12/side', format: 'prep', details: 'Cuff prime before the press and the dips' },
  ],
};

// DAY 5 — grinder circuit: swings, burpees, carries, bike.
const grinderPrep = {
  name: 'Movement Prep',
  type: 'prep' as const,
  exercises: [
    { name: 'Easy Cardio', duration: '3 min', format: 'prep', details: 'Ease in — this one gets hard fast' },
    { name: 'Inchworm', sets: 1, reps: '5', format: 'prep', details: 'Hamstrings + wrists + shoulders, the whole burpee in one drill' },
    { name: 'Glute Bridge', sets: 2, reps: '10', format: 'prep', details: 'Hinge prime before the swings' },
    { name: 'Wrist Circles', sets: 1, reps: '10/direction', format: 'prep', details: 'Burpees put your bodyweight through the wrists, repeatedly' },
    { name: 'Band Pull-Apart', sets: 2, reps: '15', format: 'prep', details: 'Scap prime before the carries' },
  ],
};

const enginePrep = {
  name: 'Movement Prep',
  type: 'prep' as const,
  exercises: [
    { name: 'Easy Cardio', duration: '3 min', format: 'prep', details: 'Ease in — the run finishes the warm-up' },
    { name: 'Leg Swings', sets: 2, reps: '10/side', format: 'prep', details: 'Dynamic hips' },
    { name: 'Glute Bridge', sets: 1, reps: '15', format: 'prep', details: 'Glute activation before the miles' },
  ],
};

const ruckPrep = {
  name: 'Ruck Prep',
  type: 'prep' as const,
  exercises: [
    { name: 'Easy Cardio', duration: '3 min', format: 'prep', details: 'Brisk unloaded walk before loading' },
    { name: 'Ankle Rocks', sets: 1, reps: '10/side', format: 'prep', details: 'Dorsiflexion for a loaded gait' },
    { name: 'Hip Flexor Opener', duration: '30s/side', format: 'prep', details: 'Open the hips before the march' },
    { name: 'Glute Bridge', sets: 2, reps: '10', format: 'prep', details: 'Carry the load with the posterior chain, not the low back' },
  ],
};

const squatPressRecovery = {
  name: 'Recovery',
  type: 'recovery' as const,
  exercises: [
    { name: 'Couch Stretch', duration: '45s/side', format: 'recovery', details: 'Hip flexors + quads after squatting' },
    { name: 'Wall Pec Stretch', duration: '45s/side', format: 'recovery', details: 'Open the chest after the bench and push-ups' },
    { name: 'Forearm Stretch', duration: '30s/side', format: 'recovery', details: 'The farmer carry is a grip session in disguise' },
    { name: '90/90 Breathing', duration: '2 min', format: 'recovery', details: 'Down-regulate' },
  ],
};

const hingeOverheadRecovery = {
  name: 'Recovery',
  type: 'recovery' as const,
  exercises: [
    { name: 'Hamstring Stretch', duration: '45s/side', format: 'recovery', details: 'The RDL is a hamstring stretch under load — finish the job' },
    { name: 'Dead Hang', duration: '30s', format: 'recovery', details: 'Lats after the rows and pull-ups, and it flushes the grip the suitcase carry just cooked' },
    { name: 'Wall Pec Stretch', duration: '45s/side', format: 'recovery', details: 'Dips shorten the front of the shoulder' },
    { name: '90/90 Breathing', duration: '2 min', format: 'recovery', details: 'Down-regulate' },
  ],
};

// The only circuit day, and the only one that reaches max heart rate — so it
// gets the longest down-regulation of the week, not the same two minutes.
const grinderRecovery = {
  name: 'Recovery',
  type: 'recovery' as const,
  exercises: [
    { name: 'Forearm Stretch', duration: '45s/side', format: 'recovery', details: 'Grip took the carries and the swings' },
    { name: 'Dead Hang', duration: '30s', format: 'recovery', details: 'Decompress and flush the forearms' },
    { name: 'Child\'s Pose', duration: '1 min', format: 'recovery', details: 'Spine after the swings and leg raises' },
    { name: '90/90 Breathing', duration: '3 min', format: 'recovery', details: 'Longest of the week — this is the day you actually redlined' },
  ],
};

const engineRecovery = {
  name: 'Recovery',
  type: 'recovery' as const,
  exercises: [
    { name: 'Hamstring Stretch', duration: '45s/side', format: 'recovery', details: 'After the miles' },
    { name: 'Calf Stretch', duration: '45s/side', format: 'recovery', details: 'Straight + bent knee' },
    { name: '90/90 Breathing', duration: '2 min', format: 'recovery', details: 'Down-regulate' },
  ],
};

const ruckRecovery = {
  name: 'Recovery',
  type: 'recovery' as const,
  exercises: [
    { name: 'Calf Stretch', duration: '45s/side', format: 'recovery', details: 'The ruck loads the calves' },
    { name: 'Couch Stretch', duration: '45s/side', format: 'recovery', details: 'Hip flexors + quads after loaded walking' },
    { name: 'Lower Back Stretch', duration: '1 min', format: 'recovery', details: 'Child\'s pose / cat-cow — decompress the spine' },
  ],
};

// ============================================================================
// TRAINING WEEK — 5 days, every sector hit, scaled for intermediate
// ============================================================================

// DAY 1 — Full-Body Strength A · Gym (INDOOR) · ALPHA + CHARLIE + DELTA + BRAVO
const trainingDay1: WorkoutTemplateDay = {
  name: 'Full-Body Strength A + Engine',
  sessionType: 'pt',
  estimatedDuration: 55,
  blocks: [
    squatPressPrep,
    {
      name: 'Strength',
      type: 'strength',
      exercises: [
        { name: 'Goblet Squat', sets: 3, reps: 8, intensity: 'progressive', rest: '2 min', details: 'ALPHA — full depth, tall chest' },
        { name: 'Dumbbell Bench Press', sets: 3, reps: 8, intensity: 'progressive', rest: '2 min', details: 'ALPHA — controlled eccentric, full lockout' },
      ],
    },
    {
      name: 'Bodyweight',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Pull-Up', sets: 3, reps: 5, intensity: 'progressive', rest: '90s', details: 'CHARLIE — scale to inverted row or band-assisted if you can\'t hit 5 clean reps' },
        { name: 'Push-Up', sets: 3, reps: 'AMRAP', intensity: 'bodyweight', rest: '60s', details: 'CHARLIE — chest to deck (knees if needed)' },
      ],
    },
    {
      name: 'Loaded Carry',
      type: 'conditioning',
      exercises: [
        { name: 'Farmer Carry', sets: 3, reps: '30m', intensity: 'progressive', rest: '75s', details: 'DELTA — moderate, tall posture' },
      ],
    },
    {
      name: 'Z2 Engine',
      type: 'conditioning',
      exercises: [
        { name: 'Assault Bike', sets: 1, duration: '10 min', format: 'cardio', intensity: 'Z2', details: 'BRAVO — easy conversational pace. Bike or rower.' },
      ],
    },
    squatPressRecovery,
  ],
};

// DAY 2 — Aerobic Base · Outdoor or treadmill · BRAVO + CHARLIE (the easy day)
const trainingDay2: WorkoutTemplateDay = {
  name: 'Aerobic Base',
  sessionType: 'pt',
  estimatedDuration: 40,
  blocks: [
    enginePrep,
    {
      name: 'Z2 Aerobic',
      type: 'conditioning',
      exercises: [
        { name: 'Run', sets: 1, duration: '28 min', format: 'cardio', intensity: 'Z2', details: 'BRAVO — steady conversational pace; walk-run intervals are fine. Outside or treadmill; rower/bike if running is limited.' },
      ],
    },
    {
      // Superset, NOT circuit — the circuit adapter only parses "N×M" reps, so
      // an AMRAP member renders blank and "3 × 30s" renders as "30 reps".
      name: 'Core Finisher',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Push-Up', sets: 3, reps: 'AMRAP', format: 'bodyweight', rest: '45s', details: 'CHARLIE — do these anywhere' },
        { name: 'Plank', sets: 3, reps: '30s', format: 'bodyweight', rest: '45s', details: 'CHARLIE — braced, no sag' },
      ],
    },
    engineRecovery,
  ],
};

// DAY 3 — Full-Body Strength B · Gym (INDOOR) · ALPHA + CHARLIE + DELTA + BRAVO
const trainingDay3: WorkoutTemplateDay = {
  name: 'Full-Body Strength B + Engine',
  sessionType: 'pt',
  estimatedDuration: 55,
  blocks: [
    hingeOverheadPrep,
    {
      name: 'Strength',
      type: 'strength',
      exercises: [
        { name: 'Romanian Deadlift', sets: 3, reps: 8, intensity: 'progressive', rest: '2 min', details: 'ALPHA — hinge, stretch the hamstrings' },
        { name: 'Overhead Press', sets: 3, reps: 8, intensity: 'progressive', rest: '2 min', details: 'ALPHA — strict, no lean' },
        { name: 'Dumbbell Row', sets: 3, reps: 10, intensity: 'progressive', rest: '90s', details: 'ALPHA — pause at the top, per side' },
      ],
    },
    {
      name: 'Bodyweight',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Pull-Up', sets: 3, reps: 5, intensity: 'progressive', rest: '90s', details: 'CHARLIE — scale to inverted row or lat pulldown if needed' },
        { name: 'Dip', sets: 3, reps: 'AMRAP', intensity: 'bodyweight', rest: '60s', details: 'CHARLIE — bench dips if you can\'t do bar dips yet' },
      ],
    },
    {
      name: 'Loaded Carry',
      type: 'conditioning',
      exercises: [
        { name: 'Suitcase Carry', sets: 3, reps: '30m', intensity: 'progressive', rest: '75s', details: 'DELTA — per side, anti-lateral flexion' },
      ],
    },
    {
      name: 'Z2 Engine',
      type: 'conditioning',
      exercises: [
        { name: 'Assault Bike', sets: 1, duration: '10 min', format: 'cardio', intensity: 'Z2', details: 'BRAVO — easy conversational pace. Bike or rower.' },
      ],
    },
    hingeOverheadRecovery,
  ],
};

// DAY 4 — Ruck · OUTDOOR, self-contained · DELTA + CHARLIE
const trainingDay4: WorkoutTemplateDay = {
  name: 'Ruck',
  sessionType: 'pt',
  estimatedDuration: 50,
  blocks: [
    ruckPrep,
    {
      name: 'Ruck',
      type: 'conditioning',
      exercises: [
        { name: 'Ruck March', duration: '2.5 miles', format: 'ruck', intensity: 'progressive', details: 'DELTA — 25 lb (before water), steady pace' },
      ],
    },
    {
      // Superset not circuit — see the Day 2 note.
      name: 'Trailside Bodyweight',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Push-Up', sets: 3, reps: 'AMRAP', format: 'bodyweight', rest: '45s', details: 'CHARLIE — at the turnaround / finish, pack off' },
        { name: 'Air Squat', sets: 3, reps: 15, format: 'bodyweight', rest: '45s', details: 'CHARLIE — flush the legs' },
        { name: 'Plank', sets: 3, reps: '30s', format: 'bodyweight', rest: '45s', details: 'CHARLIE — braced, no sag' },
      ],
    },
    ruckRecovery,
  ],
};

// DAY 5 — Grinder (Light) · Gym (INDOOR) · all four sectors, 3 rounds
const trainingDay5: WorkoutTemplateDay = {
  name: 'Four-Sector Grinder (Light)',
  sessionType: 'pt',
  estimatedDuration: 45,
  blocks: [
    grinderPrep,
    {
      name: 'Grinder Circuit',
      type: 'conditioning',
      format: 'circuit',
      exercises: [
        { name: 'Kettlebell Swing', sets: 3, reps: 15, intensity: 'progressive', format: 'circuit', details: 'ALPHA — explosive hip hinge. ALL FOUR SECTORS: 3 rounds, steady effort.' },
        { name: 'Burpee', sets: 3, reps: 10, format: 'circuit', details: 'CHARLIE — chest to deck (step back if needed)' },
        { name: 'Farmer Carry', sets: 3, reps: '30m', intensity: 'progressive', format: 'circuit', details: 'DELTA — moderate' },
        { name: 'Assault Bike', sets: 3, duration: '45s', format: 'circuit', details: 'BRAVO — steady-hard 45s (bike or rower), then repeat' },
      ],
    },
    {
      name: 'Core',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Hanging Leg Raise', sets: 3, reps: 10, intensity: 'progressive', rest: '45s', details: 'Controlled — bent-knee or lying leg raise if needed' },
        { name: 'Plank', sets: 3, reps: '30s', format: 'bodyweight', rest: '45s', details: 'Braced' },
      ],
    },
    grinderRecovery,
  ],
};

const restDay: WorkoutTemplateDay = {
  name: 'Rest Day',
  sessionType: 'pt',
  estimatedDuration: 0,
  blocks: [],
};

// ============================================================================
// PROGRAM EXPORT
// ============================================================================

export const taskforceSpearheadLight8Week: WorkoutProgram = {
  metadata,
  periodization: {
    style: 'block',
    phases: periodizationPhases,
  },
  templates: {
    'training-day1': trainingDay1,
    'training-day2': trainingDay2,
    'training-day3': trainingDay3,
    'training-day4': trainingDay4,
    'training-day5': trainingDay5,
    'training-day6': restDay,
    'training-day7': restDay,
  },
  workouts: [],
};
